import { isTourFlightStepAvailable, getTourFlightLegForPage } from 'checkout/selectors/tourFlightSelectors'
import { prepareTourFlightLeg } from 'checkout/actions/tourFlightActions'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { createSelector } from 'reselect'
import { getHasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade, getHasOptionalExtras, getSelectablePurchasableOptionsHasRoomTypeUpgrade, getShouldShowLuxPlusUpsellForTour } from 'checkout/selectors/tourV2Selectors'
import { getIsDepositBalance, getIsPostPurchaseTourAccommodation, getIsPostPurchaseTourChangeDates, getIsPostPurchaseTourOptionalExperience } from 'checkout/selectors/cartModeSelectors'
import { isPaymentScheduleBalance } from 'checkout/selectors/payment/paymentSchedule/isPaymentScheduleBalance'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { CheckoutPageId } from 'checkout/constants/pages'
import { getCurrentCheckoutPage } from 'checkout/lib/navigation/getCurrentCheckoutPage'
import { getCheckoutPathWithCartId } from 'lib/url/pathsUtils'
import productLabels from 'i18n/productLabels'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import type { AppAction } from 'actions/types/ActionTypes'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { hasInsuranceItem } from 'checkout/selectors/navigationSelectors'
import config from 'constants/config'
import { diffInDays } from 'lib/datetime/dateUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { TOUR_POST_PRE_ACCOMMODATIONS_EXCLUDE_COUNTRY_CODES } from 'constants/config/region'
import extractTourV2VariationDetails from 'lib/tours/extractTourV2VariationDetails'
import { getIsMobileAppLikeCheckoutEnabled } from 'checkout/selectors/mobileAppLikeCheckoutSelectors'
import {
  MOBILE_APP_LIKE_CHECKOUT_HEADER_STEPS,
  getMobileAppLikeCheckoutPaymentStep,
} from './mobileAppLikeCheckout'

/** 14 months in days */
const AVAILABLE_ACCOMMODATION_DAYS_THRESHOLD = 425

const TOUR_CHECKOUT_STEP_LABEL = {
  package: 'tour-checkout-step-package',
  roomType: 'tour-checkout-step-room-type',
  extras: 'tour-checkout-step-extras',
  purchase: 'tour-checkout-step-purchase',
  departureDates: 'tour-checkout-step-departure-dates',
  accommodation: 'tour-checkout-step-accommodation',
  insurance: 'tour-checkout-step-insurance',
  flights: 'tour-checkout-step-flights',
} as const

export function useTourCheckoutStepLabels(): Record<string, string> {
  const intl = useIntl()
  return useMemo(() => ({
    [TOUR_CHECKOUT_STEP_LABEL.flights]: intl.formatMessage(productLabels.flights),
    [TOUR_CHECKOUT_STEP_LABEL.package]: intl.formatMessage({
      id: 'LgJEwwJdCMdneFPR3m6y',
      defaultMessage: 'Packages',
    }),
    [TOUR_CHECKOUT_STEP_LABEL.roomType]: intl.formatMessage({
      id: 'ZJ7lQd4KhdZ8lg-Pb_UR',
      defaultMessage: 'Room type',
    }),
    [TOUR_CHECKOUT_STEP_LABEL.extras]: intl.formatMessage({
      id: 'oKH7btQPfEzwQZXcPxx0',
      defaultMessage: 'Experiences',
    }),
    [TOUR_CHECKOUT_STEP_LABEL.purchase]: intl.formatMessage({
      id: 'ni6FkAyNfEKqrT40Xovc',
      defaultMessage: 'Review & payment',
    }),
    [TOUR_CHECKOUT_STEP_LABEL.departureDates]: intl.formatMessage({
      id: '0fOtepLXxkXgoKeUyRSs',
      defaultMessage: 'Departure Dates',
    }),
    [LUXURY_PLUS.PROGRAM_NAME]: intl.formatMessage(productLabels.luxPlus),
    [TOUR_CHECKOUT_STEP_LABEL.accommodation]: intl.formatMessage({
      id: 'oBfnKt_27dieJOr-SVMy',
      defaultMessage: 'Accommodation',
    }),
    [TOUR_CHECKOUT_STEP_LABEL.insurance]: intl.formatMessage({
      id: '9ugd-1N6XlwpnGLixXro',
      defaultMessage: 'Insurance',
    }),
  }), [intl])
}

const tourCheckoutSteps: { [key: string]: App.Checkout.CheckoutStep } = {
  flights: {
    label: TOUR_CHECKOUT_STEP_LABEL.flights,
    key: CheckoutPageId.AddFlights,
    isRoute: true,
    subSteps: [CheckoutPageId.AddFlights, CheckoutPageId.DepartingFlights, CheckoutPageId.ReturningFlights].map(key => ({
      label: TOUR_CHECKOUT_STEP_LABEL.flights, key, isRoute: true,
    })),
    showFooter: false,
  },
  package: {
    label: TOUR_CHECKOUT_STEP_LABEL.package,
    key: CheckoutPageId.TourV2Upgrade,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: false,
  },
  luxPlus: {
    label: LUXURY_PLUS.PROGRAM_NAME,
    key: CheckoutPageId.LuxPlus,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: false,
  },
  extras: {
    label: TOUR_CHECKOUT_STEP_LABEL.extras,
    key: CheckoutPageId.TourV2OptionalExperience,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: true,
  },
  purchase: {
    label: TOUR_CHECKOUT_STEP_LABEL.purchase,
    key: CheckoutPageId.Purchase,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: false,
  },
  departureDates: {
    label: TOUR_CHECKOUT_STEP_LABEL.departureDates,
    key: CheckoutPageId.TourV2Departures,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: false,
  },
  accommodation: {
    label: TOUR_CHECKOUT_STEP_LABEL.accommodation,
    key: CheckoutPageId.TourV2Accommodation,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: true,
  },
  insurance: {
    label: TOUR_CHECKOUT_STEP_LABEL.insurance,
    key: CheckoutPageId.InsuranceSelectDates,
    isRoute: true,
    subSteps: EmptyArray,
    showFooter: false,
  },
}

export const isStepTour = (step?: string) => {
  return Object.values(tourCheckoutSteps).some(
    tourStep => tourStep.key === step && step !== tourCheckoutSteps.purchase.key && step !== tourCheckoutSteps.flights.key,
  )
}

function appendTourPurchaseSteps(
  steps: Array<App.Checkout.CheckoutStep>,
  isMobileAppLikeCheckoutEnabled: boolean,
): Array<App.Checkout.CheckoutStep> {
  if (isMobileAppLikeCheckoutEnabled) {
    // Tours build their header steps from conditional arrays, so append the experiment pages
    // where the existing purchase step would normally sit and use a dedicated Payment route.
    return [
      ...steps,
      ...MOBILE_APP_LIKE_CHECKOUT_HEADER_STEPS,
      getMobileAppLikeCheckoutPaymentStep(tourCheckoutSteps.purchase),
    ]
  }

  return [
    ...steps,
    tourCheckoutSteps.purchase,
  ]
}

export const getTourCheckoutSteps = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getHasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade(state),
  (state: App.State) => getSelectablePurchasableOptionsHasRoomTypeUpgrade(state),
  (state: App.State) => getHasOptionalExtras(state),
  (state: App.State) => getShouldShowLuxPlusUpsellForTour(state),
  (state: App.State) => shouldShowAccommodationStep(state),
  (state: App.State) => shouldShowExtrasStep(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (state: App.State) => isPaymentScheduleBalance(state),
  (state: App.State) => getIsDepositBalance(state),
  (state: App.State) => getIsPostPurchaseTourAccommodation(state),
  (state: App.State) => getIsMobileAppLikeCheckoutEnabled(state),
  (state: App.State) => hasInsuranceItem(state),
  isTourFlightStepAvailable,
  (
    tourItems,
    hasPackageUpgrade,
    hasRoomUpgrade,
    hasExtras,
    showLuxPlusUpsell,
    showAccommodationStep,
    showExtrasStep,
    isPostPurchaseTourOptionalExperience,
    isPostPurchaseTourChangeDates,
    isPaymentScheduleBalance,
    isDepositBalance,
    isPostPurchaseTourAccommodation,
    isMobileAppLikeCheckoutEnabled,
    hasActiveInsuranceItem,
    showFlights,
  ): Array<App.Checkout.CheckoutStep> => {
    if (isDepositBalance || isPaymentScheduleBalance) {
      return appendTourPurchaseSteps([], isMobileAppLikeCheckoutEnabled)
    }
    if (isPostPurchaseTourOptionalExperience) {
      return appendTourPurchaseSteps([tourCheckoutSteps.extras], isMobileAppLikeCheckoutEnabled)
    }
    if (isPostPurchaseTourAccommodation) {
      return appendTourPurchaseSteps([tourCheckoutSteps.accommodation], isMobileAppLikeCheckoutEnabled)
    }
    if (isPostPurchaseTourChangeDates) {
      const steps = [tourCheckoutSteps.departureDates]
      if ((!!tourItems.length && tourItems[0].isOriginalItem) || hasPackageUpgrade || hasRoomUpgrade) {
        tourCheckoutSteps.package.label = hasRoomUpgrade ? TOUR_CHECKOUT_STEP_LABEL.roomType : TOUR_CHECKOUT_STEP_LABEL.package
        steps.push(tourCheckoutSteps.package)
      }
      if (hasActiveInsuranceItem) {
        steps.push(tourCheckoutSteps.insurance)
      }
      return appendTourPurchaseSteps(steps, isMobileAppLikeCheckoutEnabled)
    }
    if (!tourItems.length) {
      return []
    }
    const steps: Array<App.Checkout.CheckoutStep> = []
    if (hasPackageUpgrade || hasRoomUpgrade) {
      tourCheckoutSteps.package.label = hasRoomUpgrade ? TOUR_CHECKOUT_STEP_LABEL.roomType : TOUR_CHECKOUT_STEP_LABEL.package
      tourCheckoutSteps.package.showFooter = !!hasRoomUpgrade
      steps.push(tourCheckoutSteps.package)
    }
    if (showLuxPlusUpsell && !showFlights) steps.push(tourCheckoutSteps.luxPlus)
    if (hasExtras && showExtrasStep) steps.push(tourCheckoutSteps.extras)
    if (showAccommodationStep) {
      steps.push(tourCheckoutSteps.accommodation)
    }
    if (showFlights) {
      steps.push(tourCheckoutSteps.flights)
      if (showLuxPlusUpsell) steps.push(tourCheckoutSteps.luxPlus)
    }
    return appendTourPurchaseSteps(steps, isMobileAppLikeCheckoutEnabled)
  },
)

export const shouldShowExtrasStep = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (items: Array<App.Checkout.TourV2Item>, offers, isExtrasPostPurchase: boolean): boolean => {
    if (!items.length) return false
    return items.some(item => {
      const { offerId, purchasableOption: { fkVariationId } } = item
      const tourV2Offer = offers[offerId] as App.Tours.TourV2Offer
      if (!tourV2Offer?.variations?.[fkVariationId]) return false
      const variationDetails = extractTourV2VariationDetails(tourV2Offer?.variations?.[fkVariationId])
      if (
        tourV2Offer.productType === 'custom_tour' &&
        !variationDetails.isBase && variationDetails.hasExtension &&
        !isExtrasPostPurchase
      ) {
        return false
      }
      return true
    })
  },
)

export const shouldShowAccommodationStep = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getAllOffers(state),
  (items: Array<App.Checkout.TourV2Item>, offers): boolean => {
    if (!items.length) return false
    return config.TOUR_ADD_ACCOMMODATION_ENABLED && items.some(item => {
      const { offerId, purchasableOption: { fkVariationId } } = item
      const tourV2Offer = offers[offerId] as App.Tours.TourV2Offer
      const { startLocationPlaceId, startLocationCountryCode, endLocationPlaceId, endLocationCountryCode } = tourV2Offer?.variations?.[fkVariationId] ?? {}
      const hasJapanLocations = TOUR_POST_PRE_ACCOMMODATIONS_EXCLUDE_COUNTRY_CODES.includes(startLocationCountryCode ?? '') || TOUR_POST_PRE_ACCOMMODATIONS_EXCLUDE_COUNTRY_CODES.includes(endLocationCountryCode ?? '')
      return (!!startLocationPlaceId || !!endLocationPlaceId) && !hasJapanLocations && diffInDays(new Date(), new Date(item.startDate)) < AVAILABLE_ACCOMMODATION_DAYS_THRESHOLD
    })
  },
)

export function navigateToTourCheckoutStep(selectedStep?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const steps = getTourCheckoutSteps(state)
    const firstStep = selectedStep ?? steps?.[0]?.key

    const url = getCheckoutPathWithCartId(selectedStep ?? firstStep, state.checkout.cart.cartId)
    if (firstStep) {
      dispatch(pushWithRegion(url))
    }
  }
}

export function navigateToNextStepTourCheckout(shouldReplaceWithRegion?: boolean, skipFlightSelection?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const steps = getTourCheckoutSteps(state)
    const showFlights = isTourFlightStepAvailable(state)
    const routes = showFlights ? steps.flatMap(step => step.subSteps?.length ? step.subSteps : [step]) : steps

    const currentPage = getCurrentCheckoutPage(state)
    const currentRoute = showFlights && skipFlightSelection ? CheckoutPageId.ReturningFlights : currentPage
    const currentIndex = routes.findIndex(step => step.key === currentRoute)
    // Before entering checkout, no route matches: index -1 advances to the first step.
    const nextStep = routes[currentIndex + 1]
    if (!nextStep) {
      return
    }

    const leg = showFlights ? getTourFlightLegForPage(nextStep.key) : undefined
    const search = leg ? dispatch(prepareTourFlightLeg(leg)) : undefined
    if (leg && !search) return
    const url = getCheckoutPathWithCartId(nextStep.key, state.checkout.cart.cartId)
    if (shouldReplaceWithRegion) {
      dispatch(replaceWithRegion(url, search))
    } else {
      dispatch(pushWithRegion(url, search))
    }
  }
}

export const getCurrentTourCheckoutStep = createSelector(
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCurrentCheckoutPage(state),
  (tourSteps, currentStep) => {
    const currentIndex = tourSteps.findIndex(step => step.key === currentStep || step.subSteps?.some(subStep => subStep.key === currentStep))
    return tourSteps?.[currentIndex]
  },
)
